/**
 * Create a WebSocket connection to a given URL and wire up message/error/close handlers.
 * URL composition is done by the caller (run-pending-auth or create-bridge-connection).
 */

import WebSocket from 'ws';
import { attachWebSocketClientPing, buildCliWebSocketClientOptions } from './cli-ws-client.js';
import { tryParseCompactHeartbeatAck } from './parse-compact-heartbeat-ack.js';

/** Header set by API on 401 when token is invalid/expired/revoked. */
export const BRIDGE_AUTH_ERROR_HEADER = 'x-bridge-auth-error';
export const BRIDGE_AUTH_ERROR_TOKEN_INVALID = 'token_invalid';

export interface CreateWsBridgeOptions {
  /** Full WebSocket URL to connect to (path and query params already set by caller). */
  url: string;
  onMessage?: (data: unknown, ws: WebSocket) => void;
  onOpen?: () => void;
  onClose?: (code: number, reason: string) => void;
  onError?: (err: Error) => void;
  /** Called when server returns 401 with X-Bridge-Auth-Error: token_invalid. May return a Promise. */
  onAuthInvalid?: () => void | Promise<void>;
  /**
   * If set, sends WebSocket ping frames on this interval while open so proxies and NAT
   * do not treat the connection as idle (avoids many code 1006 drops).
   */
  clientPingIntervalMs?: number;
  /**
   * Compact `{t:'ha',s}` acks are applied synchronously (not deferred) so heartbeat RTT is not
   * delayed behind heavy `setImmediate` bridge traffic.
   */
  onCompactHeartbeatAck?: (seq: number) => void;
  /** Drop deferred inbound frames when this socket is no longer the active bridge connection. */
  isActiveSocket?: (ws: WebSocket) => boolean;
}

export function createWsBridge(options: CreateWsBridgeOptions): WebSocket {
  const {
    url,
    onMessage,
    onOpen,
    onClose,
    onError,
    onAuthInvalid,
    clientPingIntervalMs,
    onCompactHeartbeatAck,
    isActiveSocket,
  } = options;

  const ws = new WebSocket(url, buildCliWebSocketClientOptions(url));

  let clearClientPing: (() => void) | null = null;
  function disposeClientPing(): void {
    clearClientPing?.();
    clearClientPing = null;
  }

  ws.on('unexpected-response', (request: unknown, response: { statusCode?: number; headers?: Record<string, string> }) => {
    const status = response?.statusCode ?? 0;
    const headers = response?.headers ?? {};
    const errCode = (headers[BRIDGE_AUTH_ERROR_HEADER] ?? headers['X-Bridge-Auth-Error'] ?? '').toLowerCase();
    if (status === 401 && errCode === BRIDGE_AUTH_ERROR_TOKEN_INVALID) {
      void Promise.resolve(onAuthInvalid?.());
    }
  });

  ws.on('open', () => {
    disposeClientPing();
    if (clientPingIntervalMs != null && clientPingIntervalMs > 0) {
      clearClientPing = attachWebSocketClientPing(ws, clientPingIntervalMs);
    }
    onOpen?.();
  });

  ws.on('message', (raw) => {
    const ackSeq = tryParseCompactHeartbeatAck(raw);
    if (ackSeq != null) {
      onCompactHeartbeatAck?.(ackSeq);
      return;
    }

    // Defer parse + handler so this callback returns immediately: large JSON.parse / downstream
    // bridge work does not block further frames, timers, or the firehose socket on another handle.
    setImmediate(() => {
      if (isActiveSocket && !isActiveSocket(ws)) return;
      try {
        let data: unknown;
        if (typeof raw === 'string') {
          data = JSON.parse(raw);
        } else if (Buffer.isBuffer(raw) || raw instanceof ArrayBuffer) {
          const str = Buffer.isBuffer(raw) ? raw.toString('utf8') : Buffer.from(raw).toString('utf8');
          data = JSON.parse(str);
        } else {
          data = raw;
        }
        onMessage?.(data, ws);
      } catch {
        onMessage?.(raw, ws);
      }
    });
  });

  ws.on('close', (code, reason) => {
    disposeClientPing();
    onClose?.(code, reason.toString());
  });

  ws.on('error', (err) => {
    disposeClientPing();
    onError?.(err);
  });

  return ws;
}

/** Send a JSON WebSocket frame (bridge and firehose clients share this helper). */
export function sendWsMessage(ws: WebSocket, payload: unknown): void {
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(payload));
  }
}
